import { Link } from "react-router-dom";
import { ArrowUpRight, Quote, Star } from "lucide-react";
import { motion } from "framer-motion";
import { Seo } from "@/components/site/Seo";
import { Media, Reveal, SectionHeading } from "@/components/site/Primitives";
import { CtaBanner, FAQ, Icon, TrustStrip } from "@/components/site/Sections";
import { IMAGES, SHOW_PLACEHOLDER_BADGES } from "@/lib/site";
import { STEPS, TESTIMONIALS, WHY_ITEMS } from "@/lib/content";

const Hero = () => (
  <section className="relative p-2 md:p-3" data-testid="home-hero">
    <div className="media-frame relative flex min-h-[92svh] items-end">
      <img src={IMAGES.hero.src} alt={IMAGES.hero.alt} className="absolute inset-0 h-full w-full animate-slow-zoom object-cover" />
      {SHOW_PLACEHOLDER_BADGES && IMAGES.hero.placeholder && <span className="placeholder-badge !left-auto !right-4 !top-24">Placeholder photo</span>}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/15" />
      <div className="container-x relative pb-14 pt-32 md:pb-20">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}>
          <span className="eyebrow !text-gold">Rhodes, Greece</span>
          <h1 className="heading-xl mt-6 max-w-3xl text-white" data-testid="hero-headline">Private Transfers &amp; Tours in Rhodes</h1>
          <p className="mt-6 max-w-xl text-lg text-white/80 md:text-xl" data-testid="hero-subline">Arrive in comfort. Explore in style. Available 24/7</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/booking" className="btn btn-gold" data-testid="hero-book-transfer-button">Book a Transfer</Link>
            <Link to="/tours" className="btn btn-ghost-light" data-testid="hero-explore-tours-button">Explore Tours</Link>
          </div>
          <div className="mt-12 border-t border-white/15 pt-6">
            <TrustStrip dark />
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const SERVICES = [
  { title: "Airport & Port Transfers", text: "Met on arrival, driven straight to your door. Fixed prices from Rhodes Airport.", to: "/transfers", img: IMAGES.airport, id: "airport" },
  { title: "Private Transfers Anywhere", text: "Hotels, restaurants, beaches, villages. Wherever your holiday takes you.", to: "/transfers#anywhere", img: IMAGES.anywhere, id: "anywhere" },
  { title: "Private Island Tours", text: "Monuments, history, swimming and long lunches. Your day, your pace.", to: "/tours", img: IMAGES.tours, id: "tours" },
];

const ServiceCard = ({ s, i }) => (
  <Reveal delay={i * 0.1} className={i === 0 ? "md:col-span-2 lg:col-span-1" : ""}>
    <Link to={s.to} className="group block" data-testid={`service-card-${s.id}`}>
      <Media img={s.img} className="aspect-[4/5]" imgClassName="transition-transform duration-1000 ease-out group-hover:scale-105">
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
          <h3 className="font-serif text-3xl text-white md:text-[34px]">{s.title}</h3>
          <p className="mt-3 max-w-xs text-sm text-white/75">{s.text}</p>
          <span className="mt-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/40 text-white transition-colors group-hover:border-gold group-hover:bg-gold group-hover:text-ink">
            <ArrowUpRight className="h-5 w-5" />
          </span>
        </div>
      </Media>
    </Link>
  </Reveal>
);

const Services = () => (
  <section className="container-x section-y" data-testid="services-section">
    <SectionHeading eyebrow="What we do" title="Wherever Rhodes takes you, we take you there" />
    <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {SERVICES.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
    </div>
  </section>
);

const Why = () => (
  <section className="bg-ink py-20 text-white md:py-28" data-testid="why-section">
    <div className="container-x grid gap-14 lg:grid-cols-12">
      <SectionHeading light className="lg:col-span-4" eyebrow="Why Alpha Transfer" title="Taken care of, from the moment you land" text="A personal, private service. The details are what make it feel effortless." />
      <div className="grid gap-px overflow-hidden rounded-card bg-white/10 sm:grid-cols-2 lg:col-span-8">
        {WHY_ITEMS.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.05} className={`bg-ink p-7 md:p-8 ${i === WHY_ITEMS.length - 1 ? "sm:col-span-2" : ""}`}>
            <div data-testid={`why-item-${i}`}>
              <Icon name={w.icon} className="h-7 w-7 text-gold" />
              <h3 className="mt-5 font-serif text-2xl">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/60">{w.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section className="container-x section-y" data-testid="how-it-works-section">
    <div className="grid gap-14 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHeading eyebrow="How it works" title="Simple, from start to finish" />
        <Reveal delay={0.1}>
          <Media img={IMAGES.interior} className="mt-10 aspect-[4/3]" />
        </Reveal>
      </div>
      <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1} className="border-b border-slate-200 py-9 first:pt-0 last:border-0">
            <div className="flex gap-8" data-testid={`how-step-${i + 1}`}>
              <span className="font-serif text-5xl text-gold md:text-6xl">{s.n}</span>
              <div>
                <h3 className="font-serif text-3xl text-ink">{s.title}</h3>
                <p className="mt-3 text-slate-600">{s.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal><Link to="/booking" className="btn btn-dark mt-6 w-fit" data-testid="how-book-button">Start your booking</Link></Reveal>
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="bg-silver/60 py-20 md:py-28" data-testid="testimonials-section">
    <div className="container-x">
      <SectionHeading eyebrow="Guest reviews" title="What our guests say" text="Real reviews from our guests on Tripadvisor." />
      <div className="mt-14 grid gap-5 md:grid-cols-3">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1}>
            <figure className="card-soft flex h-full flex-col border border-slate-200 bg-white p-8" data-testid={`testimonial-${i}`}>
              <div className="flex items-center justify-between">
                <Quote className="h-8 w-8 text-gold" strokeWidth={1.2} />
                <span className="flex gap-1" aria-label="5 out of 5 stars">
                  {[0, 1, 2, 3, 4].map((s) => <Star key={s} className="h-4 w-4 fill-gold text-gold" />)}
                </span>
              </div>
              <p className="mt-6 font-sans text-sm font-medium text-ink">{t.title}</p>
              <blockquote className="mt-3 flex-1 font-serif text-2xl leading-snug text-ink">{t.quote}</blockquote>
              <figcaption className="mt-6 text-xs uppercase tracking-[0.2em] text-slate-500">
                {t.name} · {t.origin}
                <span className="mt-1 block normal-case tracking-normal text-slate-400">{t.date} · via Tripadvisor</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="Private Transfers & Tours in Rhodes | Alpha Transfer"
        description="Premium Rhodes airport transfers and private tours. Fixed prices per vehicle, flight tracking, meet & greet, free child seats. Available 24/7."
        path="/"
      />
      <Hero />
      <Services />
      <Why />
      <HowItWorks />
      <Testimonials />
      <FAQ />
      <div className="pt-20 md:pt-28" />
      <CtaBanner />
    </>
  );
}
